import {
    Component,
    Injectable,
    Input,
    Output,
    OnInit,
    OnDestroy,
    EventEmitter,
    ViewChild,
    TemplateRef,
    PLATFORM_ID,
    Inject,
    AfterViewInit,
    HostListener
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DatatableComponent, SelectionType, TableColumn } from '@swimlane/ngx-datatable';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DatatableModel } from '../../models/datatable-model.model';
import PerfectScrollbar from 'perfect-scrollbar';
import { MatPaginator } from '@angular/material/paginator';
import { FuseConfigService } from '../../../template-atlas';
import { ShellDomainProvider } from '../../../core';

@Injectable()
@Component({
    selector: 'app-data-table',
    templateUrl: './platform-datatable.component.html',
    styleUrls: ['./platform-datatable.component.scss'],
    standalone: false
})
export class AppPlatformDataTableComponent<T> implements OnInit, OnDestroy, AfterViewInit {
    isPlatformBrowser: boolean;
    ps: PerfectScrollbar | any;
    mobileScreenPaginatorTotalCount = ShellDomainProvider['api']['IsMobileScreenPaginatorTotalCount'];
    constructor(@Inject(PLATFORM_ID) private platformId: Object, public fuseConfig: FuseConfigService) {
        this.isPlatformBrowser = isPlatformBrowser(this.platformId);
    }

    @Input()
    footerHeight = 48;

    @Input()
    headerHeight = 48;

    @Input()
    columns: TableColumn[];

    @Input()
    messages: any = {
        emptyMessage: 'NO_DATA_TO_DISPLAY',
        totalMessage: 'TOTAL'
    };

    @Input()
    alternativeRowClass: any;
    // @Input()
    // avatarColumn:string;

    selected: any[] = [];

    private _datatableModelInput = new BehaviorSubject<DatatableModel<T>>(undefined);

    @ViewChild('emptyTemplate', { static: true })
    public emptyTemplate: TemplateRef<any>;

    // @ViewChild('avatarTemplate')
    // public avatarTemplate: TemplateRef<any>;

    // @ViewChild('avatarHeaderTemplate')
    // public avatarHeaderTemplate: TemplateRef<any>;
    @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;

    dataTableUpdateFixTimeout;

    @ViewChild('idAnchorEditTemplate', { static: true })
    public idAnchorEditTemplate: TemplateRef<any>;

    @ViewChild('dateTemplate', { static: true })
    public dateTemplate: TemplateRef<any>;

    @ViewChild('dateTimeTemplate', { static: true })
    public dateTimeTemplate: TemplateRef<any>;

    // change data to use getter and setter
    @Input()
    set datatableModelInput(value) {
        // set the latest value for _data BehaviorSubject
        if (value !== undefined) {
            this._datatableModelInput.next(value);
        }
    }

    get datatableModelInput() {
        // get the latest value from _data BehaviorSubject
        return this._datatableModelInput.getValue();
    }

    @Output()
    onFetchDataRequired = new EventEmitter<DatatableModel<T>>();

    @Output()
    onSelectData = new EventEmitter<any>();

    public datatableModel: DatatableModel<T>;
    public isLoading = true;
    public currentPageLimit = 0;
    public pageLimitOptions = [{ value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }];

    rows: any[];
    reorderable = true;
    customClasses = {
        sortAscending: 'fa fa-sort-asc',
        sortDescending: 'fa fa-sort-desc',
        pagerLeftArrow: 'fa fa-chevron-left',
        pagerRightArrow: 'fa fa-chevron-right',
        pagerPrevious: 'fa fa-step-backward',
        pagerNext: 'fa fa-step-forward'
    };

    sorts: { prop: string; dir: string }[] = [];

    settingsChangeSubscription: Subscription;

    _currentNavFold: boolean;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    displayedColumns = [];

    @HostListener('window:resize') onResize() {
        this.perfectScroll();
    }

    selectionType: SelectionType = SelectionType.single;
    endIndex: number = 0;
    ngOnInit(): void {
        this.datatableModel = new DatatableModel<T>();

        this.settingsChangeSubscription = this.fuseConfig.config.pipe().subscribe(config => {
            if (this.table && this._currentNavFold !== config.layout.navbar.folded) {
                this._currentNavFold = config.layout.navbar.folded;
                this.dataTableUpdateFixTimeout = setTimeout(() => {
                    this.table.rows = [...this.datatableModel.Data];
                }, 0);
            }
        });

        this._datatableModelInput.subscribe(
            datatableModel => {
                this.datatableModel = datatableModel;

                this.selectionType = datatableModel.SelectionType || SelectionType.single;

                if (datatableModel.SortBy && datatableModel.SortDir) {
                    this.sorts = [
                        {
                            prop: datatableModel.SortBy,
                            dir: datatableModel.SortDir
                        }
                    ];
                } else {
                    this.sorts = [];
                }
                this.displayedColumns = this.columnToDisplay(this.columns);
                this.isLoading = false;
                this.updateEndIndex();
            } //  err => console.log(err)
        );
        this.loadPage();
    }

    public loadPage(pageEvent: { page: number } = { page: 1 }) {
        this.isLoading = true;
        this.datatableModel.CurrentPageNumber = (pageEvent.page || 1) - 1;
        this.onFetchDataRequired.emit(this.datatableModel);
    }

    public loadPageForMobile(pageEvent = { pageIndex: 0 }) {
        this.isLoading = true;
        this.datatableModel.CurrentPageNumber = pageEvent.pageIndex;
        this.onFetchDataRequired.emit(this.datatableModel);
    }

    public onSort(event) {
        if (this.datatableModel.SortBy != event.sorts[0].prop) {
            //this means we are sorting on a new column
            //so we need to return the paging to the first page
            this.datatableModel.CurrentPageNumber = 0;
        }

        this.datatableModel.SortBy = event.sorts[0].prop;
        this.datatableModel.SortDir = event.sorts[0].dir;

        this.loadPage();
    }

    public onLimitChange(limit: any): void {
        this.datatableModel.PageSize = this.currentPageLimit = parseInt(limit, 10);
        this.datatableModel.CurrentPageNumber = 0;
        this.loadPage();
    }

    public onSelect($event) {
        this.onSelectData.emit(this.selected[0]);
    }
    onSelectMbl(row) {
        this.onSelectData.emit(row);
    }

    ngOnDestroy(): void {
        clearTimeout(this.dataTableUpdateFixTimeout);

        if (this.settingsChangeSubscription) {
            this.settingsChangeSubscription.unsubscribe();
        }
        if (this._datatableModelInput) this._datatableModelInput.unsubscribe();

        if (this.ps) this.ps.destroy();
        // Clean up
        this.ps = null;
    }

    ngAfterViewInit(): void {
        // if (this.isPlatformBrowser) {
        //   if (window.screen.width > 600) {
        //     const dataTableBody: HTMLElement = document.querySelector("datatable-body");
        //     this.ps = new PerfectScrollbar(dataTableBody, { suppressScrollY: true });
        //   }
        // }
        this.perfectScroll();
    }

    perfectScroll = () => {
        if (this.isPlatformBrowser) {
            if (window.innerWidth > 600) {
                const dataTableBody: HTMLElement = document.querySelector('datatable-body');
                if (dataTableBody) this.ps = new PerfectScrollbar(dataTableBody, { suppressScrollY: true });
            }
        }
    };

    columnToDisplay(columns: any) {
        var displayColumns = columns && columns.length ? columns.map(obj => obj.prop) : [];
        return displayColumns;
    }

    public updateEndIndex() {
        const rowCount = this.datatableModel?.TotalElements || 0;
        const pageSize = this.datatableModel?.PageSize || 10;
        const offset = this.datatableModel?.CurrentPageNumber || 0;
        this.endIndex = rowCount === 0 ? 0 : Math.min((offset + 1) * pageSize, rowCount);
    }
}
